import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SapFlocRow,
  ParsedFloc,
  SapImportConfig,
  ImportValidation,
  FlocType,
  Criticality,
  FlocOperationalStatus,
} from '@easydone/shared';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import {
  parseSapFlocs,
  validateImport,
  importFlocs,
} from '../services/sapImportService';
import * as XLSX from 'xlsx';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

type ImportStep = 'upload' | 'configure' | 'preview' | 'importing' | 'complete';

export default function SapImportPage() {
  const { currentUser } = useAuth();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();

  // Step management
  const [step, setStep] = useState<ImportStep>('upload');

  // File uploads
  const [flocFile, setFlocFile] = useState<File | null>(null);

  // Parsed data
  const [flocRows, setFlocRows] = useState<SapFlocRow[]>([]);
  const [parsedFlocs, setParsedFlocs] = useState<ParsedFloc[]>([]);

  // Configuration
  const [config, setConfig] = useState<SapImportConfig>({
    siteCode: '',
    siteName: '',
    defaultFlocType: 'custom',
    defaultCriticality: 'medium',
    defaultStatus: 'operational',
    importEquipment: false, // Disabled by default - import separately
    typeMapping: {
      'ACT': 'equipment_position',
      'BFP': 'equipment_position',
      'DWT': 'building',
      'PUMP': 'equipment_position',
      'VALVE': 'equipment_position',
      'TANK': 'equipment_position',
    },
  });

  // Validation
  const [validation, setValidation] = useState<ImportValidation | null>(null);

  // Progress
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [importResult, setImportResult] = useState<{
    flocs: { success: boolean; imported: number; errors: string[] };
    equipment: { success: boolean; imported: number; errors: string[] };
  } | null>(null);

  const [error, setError] = useState<string>('');

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      setFlocFile(file);
      setFlocRows(jsonData as SapFlocRow[]);
      setError('');
    } catch (err: any) {
      setError(`Failed to read file: ${err.message}`);
    }
  };

  // Proceed to configuration
  const handleProceedToConfig = () => {
    if (!flocRows.length) {
      setError('Please upload a FLOC file');
      return;
    }
    setStep('configure');
  };

  // Parse and validate
  const handleProceedToPreview = () => {
    if (!config.siteCode || !config.siteName) {
      setError('Please fill in Site Code and Site Name');
      return;
    }

    try {
      const parsed = parseSapFlocs(flocRows, config);
      setParsedFlocs(parsed);

      const valid = validateImport(parsed, []);
      setValidation(valid);

      setStep('preview');
      setError('');
    } catch (err: any) {
      setError(`Failed to parse data: ${err.message}`);
    }
  };

  // Execute import
  const handleImport = async () => {
    if (!currentOrg || !currentUser || !validation?.isValid) return;

    setStep('importing');
    setProgress({ current: 0, total: parsedFlocs.length, message: 'Starting import...' });

    try {
      // Import FLOCs
      const flocResult = await importFlocs(
        currentOrg.id,
        '', // siteId will be auto-generated
        parsedFlocs,
        (current, total, message) => {
          setProgress({ current, total, message });
        }
      );

      setImportResult({
        flocs: flocResult,
        equipment: { success: true, imported: 0, errors: [] }
      });
      setStep('complete');
    } catch (err: any) {
      setError(`Import failed: ${err.message}`);
      setStep('preview');
    }
  };

  if (!currentOrg) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center">Please select an organization first.</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">SAP Data Import</h1>
        <p className="mt-1 text-sm text-gray-600">
          Import functional locations and equipment from SAP export files
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {['upload', 'configure', 'preview', 'importing', 'complete'].map((s, idx) => (
            <div key={s} className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  step === s
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : idx < ['upload', 'configure', 'preview', 'importing', 'complete'].indexOf(step)
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-300 bg-white text-gray-400'
                }`}
              >
                {idx + 1}
              </div>
              {idx < 4 && (
                <div
                  className={`w-20 h-0.5 ${
                    idx < ['upload', 'configure', 'preview', 'importing', 'complete'].indexOf(step)
                      ? 'bg-primary-600'
                      : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center mt-2">
          {['Upload', 'Configure', 'Preview', 'Importing', 'Complete'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <span
                className={`text-xs ${
                  ['upload', 'configure', 'preview', 'importing', 'complete'].indexOf(step) === idx
                    ? 'text-primary-600 font-medium'
                    : 'text-gray-500'
                }`}
              >
                {label}
              </span>
              {idx < 4 && <span className="mx-4" />}
            </div>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: Upload Files */}
      {step === 'upload' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Upload Files</h2>

          {/* FLOC File */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              FLOC Export File (Required) *
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                className="hidden"
                id="floc-file-upload"
              />
              <label
                htmlFor="floc-file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </span>
                <span className="text-xs text-gray-500 mt-1">Excel files (.xlsx, .xls)</span>
              </label>
              {flocFile && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-green-600">
                  <CheckCircleIcon className="w-5 h-5" />
                  <span>{flocFile.name} ({flocRows.length} rows)</span>
                </div>
              )}
            </div>
          </div>

          {/* Info about equipment import */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> Equipment (Assets) should be imported separately after establishing the FLOC hierarchy.
              This allows you to properly link equipment to the correct functional locations.
            </p>
          </div>

          <div className="flex gap-3 justify-end">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleProceedToConfig}
              disabled={!flocRows.length}
              className="btn btn-primary"
            >
              Next: Configure
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Configure */}
      {step === 'configure' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 2: Configure Import</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site Code *
              </label>
              <input
                type="text"
                value={config.siteCode}
                onChange={(e) => setConfig({ ...config, siteCode: e.target.value })}
                className="input"
                placeholder="e.g., 2101"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site Name *
              </label>
              <input
                type="text"
                value={config.siteName}
                onChange={(e) => setConfig({ ...config, siteName: e.target.value })}
                className="input"
                placeholder="e.g., Douglas WTP"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default FLOC Type
              </label>
              <select
                value={config.defaultFlocType}
                onChange={(e) => setConfig({ ...config, defaultFlocType: e.target.value as FlocType })}
                className="input"
              >
                <option value="site">Site</option>
                <option value="building">Building</option>
                <option value="floor">Floor</option>
                <option value="area">Area</option>
                <option value="zone">Zone</option>
                <option value="system">System</option>
                <option value="equipment_position">Equipment Position</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Criticality
              </label>
              <select
                value={config.defaultCriticality}
                onChange={(e) => setConfig({ ...config, defaultCriticality: e.target.value as Criticality })}
                className="input"
              >
                <option value="not_in_use">Not in use</option>
                <option value="very_low">Very low</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="very_high">Very high</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Status
              </label>
              <select
                value={config.defaultStatus}
                onChange={(e) => setConfig({ ...config, defaultStatus: e.target.value as FlocOperationalStatus })}
                className="input"
              >
                <option value="operational">Operational</option>
                <option value="non_operational">Non-Operational</option>
                <option value="vacant">Vacant</option>
                <option value="maintenance">Maintenance</option>
                <option value="planned">Planned</option>
              </select>
            </div>

          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setStep('upload')} className="btn btn-secondary">
              Back
            </button>
            <button onClick={handleProceedToPreview} className="btn btn-primary">
              Next: Preview
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && validation && (
        <div className="space-y-6">
          {/* Validation Results */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Validation Results</h2>

            {validation.isValid ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Ready to Import</p>
                  <p className="text-sm text-green-700 mt-1">
                    All validation checks passed successfully
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Validation Errors</p>
                  <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                    {validation.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {validation.warnings.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Warnings</p>
                  <ul className="text-sm text-yellow-700 mt-2 list-disc list-inside">
                    {validation.warnings.map((warn, idx) => (
                      <li key={idx}>{warn}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{validation.stats.totalFlocs}</div>
                <div className="text-sm text-gray-600">Total FLOCs</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{validation.stats.rootFlocs}</div>
                <div className="text-sm text-gray-600">Root FLOCs</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{validation.stats.maxDepth}</div>
                <div className="text-sm text-gray-600">Max Depth</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{validation.stats.totalEquipment}</div>
                <div className="text-sm text-gray-600">Equipment</div>
              </div>
            </div>
          </div>

          {/* Preview Table */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview (First 20 FLOCs)</h2>

            {/* Auto-generated info */}
            {parsedFlocs.filter(f => f.name.includes('Auto-generated')).length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> Auto-generated {parsedFlocs.filter(f => f.name.includes('Auto-generated')).length} intermediate parent FLOCs to build complete hierarchy.
                  You can rename these after import.
                </p>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parsedFlocs.slice(0, 20).map((floc, idx) => (
                    <tr key={idx} className={floc.name.includes('Auto-generated') ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-3 text-sm text-gray-900">{floc.code}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {floc.name}
                        {floc.name.includes('Auto-generated') && (
                          <span className="ml-2 text-xs text-blue-600">(Auto)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{floc.level}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 capitalize">{floc.type.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{floc.parentCode || 'Root'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button onClick={() => setStep('configure')} className="btn btn-secondary">
              Back
            </button>
            <button
              onClick={handleImport}
              disabled={!validation.isValid}
              className="btn btn-primary"
            >
              Start Import
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Importing */}
      {step === 'importing' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Importing Data...</h2>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{progress.message}</span>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>

            <p className="text-sm text-gray-600 text-center">
              Please wait while we import your data. This may take a few minutes.
            </p>
          </div>
        </div>
      )}

      {/* Step 5: Complete */}
      {step === 'complete' && importResult && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Complete</h2>

          <div className="space-y-4">
            {/* FLOC Import Result */}
            <div className={`p-4 border rounded-lg ${
              importResult.flocs.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {importResult.flocs.success ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    importResult.flocs.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    FLOCs: {importResult.flocs.imported} imported
                  </p>
                  {importResult.flocs.errors.length > 0 && (
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                      {importResult.flocs.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Equipment Import Result */}
            {importResult.equipment.imported > 0 && (
              <div className={`p-4 border rounded-lg ${
                importResult.equipment.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {importResult.equipment.success ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      importResult.equipment.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      Equipment: {importResult.equipment.imported} imported
                    </p>
                    {importResult.equipment.errors.length > 0 && (
                      <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                        {importResult.equipment.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => navigate('/flocs')} className="btn btn-primary">
              View FLOCs
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
